import type { Theme } from '../store/types.ts';

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function addSvgBackground(svgContent: string, theme: Theme): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svg = doc.documentElement as unknown as SVGSVGElement;

  const viewBox = svg.viewBox?.baseVal;
  const width = viewBox?.width || parseFloat(svg.getAttribute('width') || '0') || 1200;
  const height = viewBox?.height || parseFloat(svg.getAttribute('height') || '0') || 800;

  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  svg.setAttribute('xmlns:xhtml', 'http://www.w3.org/1999/xhtml');

  const bg = doc.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('x', String(viewBox?.x ?? 0));
  bg.setAttribute('y', String(viewBox?.y ?? 0));
  bg.setAttribute('width', String(width));
  bg.setAttribute('height', String(height));
  bg.setAttribute('fill', theme === 'dark' ? '#111113' : '#ffffff');
  svg.insertBefore(bg, svg.firstChild);

  return new XMLSerializer().serializeToString(svg);
}

export function exportSvg(svgContent: string, title: string, theme: Theme) {
  const svgWithBg = addSvgBackground(svgContent, theme);
  triggerDownload(new Blob([svgWithBg], { type: 'image/svg+xml' }), `${title}.svg`);
}

export function exportMermaidSource(code: string, title: string) {
  triggerDownload(new Blob([code], { type: 'text/plain;charset=utf-8' }), `${title}.mmd`);
}

export async function exportPng(svgContent: string, title: string, theme: Theme, scale: number) {
  const svgWithBg = addSvgBackground(svgContent, theme);

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgWithBg, 'image/svg+xml');
  const svg = doc.documentElement as unknown as SVGSVGElement;

  const viewBox = svg.viewBox?.baseVal;
  const width = viewBox?.width || parseFloat(svg.getAttribute('width') || '0') || 1200;
  const height = viewBox?.height || parseFloat(svg.getAttribute('height') || '0') || 800;

  // Inline foreignObject styles so they survive img sandbox.
  // Collect computed styles from the live document and embed them.
  const foreignObjects = svg.querySelectorAll('foreignObject');
  for (const fo of foreignObjects) {
    // Ensure XHTML namespace on all child elements
    fo.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    // Add inline font styling to all text inside foreignObject
    const htmlElements = fo.querySelectorAll('*');
    for (const el of htmlElements) {
      const htmlEl = el as HTMLElement;
      if (!htmlEl.style) continue;
      if (!htmlEl.style.fontFamily) htmlEl.style.fontFamily = 'Inter, sans-serif';
      if (!htmlEl.style.color) htmlEl.style.color = theme === 'dark' ? '#EDEDEF' : '#1A1A1E';
    }
  }

  const serialized = new XMLSerializer().serializeToString(svg);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;

  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);

      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Canvas context unavailable'));
        return;
      }

      context.fillStyle = theme === 'dark' ? '#111113' : '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((pngBlob) => {
        if (!pngBlob) {
          reject(new Error('PNG export failed'));
          return;
        }
        triggerDownload(pngBlob, `${title}@${scale}x.png`);
        resolve();
      }, 'image/png');
    };
    image.onerror = () => reject(new Error('Failed to load SVG during PNG export'));
    image.src = dataUrl;
  });
}
